/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import {BeamMultiSelect} from "./BeamMultiSelect";
import React, {useEffect, useState} from "react";
import {ajaxRaw} from "../../../utils/StoreUtils";
import {ProvisionedTerminal} from "../../../shared/entities/provisionedTerminal";
import {isNil} from "lodash";
import {FormHelperText} from "@mui/material";
import {useField} from "formik";

interface BeamTerminalSelectProps {
  name: string;
  label: string;
  required?: boolean;

  filterToCustomerGroup?: string;
}

export const BeamTerminalSelect = ({ name, label, required, filterToCustomerGroup }: BeamTerminalSelectProps) => {
  const [terminalList, setTerminalList] = useState<ProvisionedTerminal[]>([]);
  const [filteredTerminalList, setFilteredTerminalList] = useState<ProvisionedTerminal[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);
  const [, , helper] = useField<string[]>(name);

  useEffect(() => {
    ajaxRaw.get(`/provisioning/terminals`).subscribe({
      next: value => {
        const sortByDisplayName = (a: ProvisionedTerminal, b: ProvisionedTerminal) =>
          a.displayName.localeCompare(b.displayName);
        setTerminalList((value.response as ProvisionedTerminal[]).sort(sortByDisplayName));
      },
      error: err => {
        setError(err.message);
      },
    });
  }, []);

  useEffect(() => {
    // If the filter is not set, show all terminals. Otherwise, only show terminals that match the filter.
    if (filterToCustomerGroup === undefined) {
      setFilteredTerminalList(terminalList);
    } else {
      setFilteredTerminalList(terminalList.filter(terminal => terminal.customerGroup === filterToCustomerGroup));
    }
  }, [filterToCustomerGroup, terminalList]);

  // Deselect all options when the customer group changes.
  const [lastFilterToCustomerGroup, setLastFilterToCustomerGroup] = useState<string | undefined>(filterToCustomerGroup);
  useEffect(() => {
    if (lastFilterToCustomerGroup === filterToCustomerGroup) {
      return;
    }
    setLastFilterToCustomerGroup(filterToCustomerGroup);
    helper.setValue([]);
  }, [filterToCustomerGroup, lastFilterToCustomerGroup, helper]);

  return (
    <>
      <BeamMultiSelect
        name={name}
        label={label}
        required={required}
        options={filteredTerminalList.map(terminal => terminal.displayName)}
      />
      {!isNil(error) && (
        <FormHelperText sx={{ marginLeft: '15px' }} error={true}>
          {error}
        </FormHelperText>
      )}
    </>
  );
};
